/** Booking-local date/time formatting for the detail view (building timezone, locale-aware). */

import { applyLocaleTimeConventions } from '@hqo/date-time/locale-time';
import { createDateTimeFormat } from '@hqo/date-time/core';

/**
 * createDateTimeFormat degrades an INVALID zone to UTC loudly, but `undefined` makes it omit
 * the key entirely and Intl then uses the DEVICE zone. buildingTimezone is reachable as
 * undefined (the mappers omit it when the building record is null), so the floor has to be
 * set here or a non-UTC reader silently gets their own day back.
 */
const UTC = 'UTC';

/** "Friday, June 5, 2026" in the building's timezone. Empty for a missing date. */
export const formatBookingDate = (iso: string | undefined, timeZone?: string, locale = 'en-US'): string => {
  if (!iso) return '';

  return createDateTimeFormat(locale, {
    timeZone: timeZone ?? UTC,
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(iso));
};

/** "1:45 PM – 2:45 PM" (building-local). Falls back to a single time if one bound is missing. */
export const formatBookingTimeRange = (
  startIso: string | undefined,
  endIso: string | undefined,
  timeZone?: string,
  locale = 'en-US',
): string => {
  // Shared France-`h` override on top of the locale's own short time (fr-* → "16 h 00", not the CLDR
  // administrative colon); a no-op for every other locale. Matches every other HqO surface.
  const fmt = (iso: string): string =>
    applyLocaleTimeConventions(
      createDateTimeFormat(locale, { timeZone: timeZone ?? UTC, hour: 'numeric', minute: '2-digit' }).format(new Date(iso)),
      locale,
    );
  if (startIso && endIso) return `${fmt(startIso)} – ${fmt(endIso)}`;
  if (startIso) return fmt(startIso);
  if (endIso) return fmt(endIso);

  return '';
};
